import { DesignChannelsNames } from 'common/ShareSheetV2/Screens/constants'
import { GenerationType } from 'components/BlazeCommandLine/api/createDocGenerationRequest'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { DesignChannel } from 'components/NewFileModal/Types'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { TextChannels } from '../../../constants'
import { camelCase, startCase } from 'lodash'
import { BrandPlanTactic } from 'types'
import { TACTIC_TO_PROVIDER } from 'components/FirstTimeExperienceModal/steps/BrandPlanTactics/SelectAccountsModal/SelectAccountsModal'
import { uuid4 } from '@sentry/utils'

export const getPostTypeName = (channel: ChannelType | DesignChannel | GenerationType | undefined) => {
  const textChannel = TextChannels[channel as ChannelType]
  const designChannel = DesignChannelsNames[channel as unknown as DesignChannel]
  let postType = designChannel || textChannel?.name || textChannel?.displayName
  let postTypeName = startCase(camelCase(postType))

  if (
    channel &&
    [ChannelType.LinkedInPosts, DesignChannel.LinkedInPost].includes(channel as ChannelType | DesignChannel)
  )
    postTypeName = 'LinkedIn Post'

  if (
    channel &&
    [ChannelType.InstagramPosts, DesignChannel.InstagramPost].includes(channel as ChannelType | DesignChannel)
  )
    postTypeName = 'Instagram Post'

  if (
    channel &&
    [ChannelType.FacebookPost, DesignChannel.FacebookPost].includes(channel as ChannelType | DesignChannel)
  )
    postTypeName = 'Facebook Post'

  if (channel && [ChannelType.Tweets, DesignChannel.TwitterPost].includes(channel as ChannelType | DesignChannel))
    postTypeName = 'X/Twitter Post'

  return postTypeName
}

interface VideoPlatformConfig {
  platform: ExternalIntegrationEnum
  channel: DesignChannel
  hasPostSettings: boolean
}

export const VIDEO_PLATFORM_CONFIGS: VideoPlatformConfig[] = [
  {
    platform: ExternalIntegrationEnum.Instagram,
    channel: DesignChannel.InstagramReel,
    hasPostSettings: false,
  },
  {
    platform: ExternalIntegrationEnum.TikTok,
    channel: DesignChannel.TikTokPost,
    hasPostSettings: true,
  },
  {
    platform: ExternalIntegrationEnum.YouTube,
    channel: DesignChannel.YoutubeShort,
    hasPostSettings: true,
  },
]

export const SOCIAL_VIDEO_PLATFORMS = VIDEO_PLATFORM_CONFIGS.map((config) => config.platform)

export const SOCIAL_VIDEO_CHANNELS = VIDEO_PLATFORM_CONFIGS.map((config) => config.channel)

export const SOCIAL_CHANNELS = [
  'facebook_post',
  'instagram_post',
  'instagram_story',
  'linkedin_post',
  'twitter_post',
  'twitter_thread',
]

export const BLOG_AND_EMAIL_CHANNELS = ['newsletter', 'blog_post']

export const getVideoPlatformConfig = (platform: ExternalIntegrationEnum): VideoPlatformConfig | undefined => {
  return VIDEO_PLATFORM_CONFIGS.find((config) => config.platform === platform)
}

export const isVideoChannel = (channel: DesignChannel): boolean => {
  return SOCIAL_VIDEO_CHANNELS.includes(channel)
}

export const isSocialChannel = (channel: string): boolean => {
  return SOCIAL_CHANNELS.includes(channel)
}

export const isBlogOrEmailChannel = (channel: string): boolean => {
  return BLOG_AND_EMAIL_CHANNELS.includes(channel)
}

export const getChannelForPlatform = (platform: ExternalIntegrationEnum): DesignChannel | undefined => {
  const config = getVideoPlatformConfig(platform)
  return config?.channel
}

export const hasPostSettings = (platform: ExternalIntegrationEnum): boolean => {
  const config = getVideoPlatformConfig(platform)
  return config?.hasPostSettings ?? false
}

// Tactics used for regular social media crossposting
export const CROSSPOST_TACTICS = [
  BrandPlanTactic.Facebook,
  BrandPlanTactic.Twitter,
  BrandPlanTactic.LinkedIn,
  BrandPlanTactic.Instagram,
]

// Tactics used for video crossposting
export const VIDEO_CROSSPOST_TACTICS = [BrandPlanTactic.InstagramReel, BrandPlanTactic.YouTube, BrandPlanTactic.TikTok]

/**
 * Get crosspost targets based on channel type and schedule tactics
 *
 * For video channels (InstagramReel, TikTokPost, YoutubeShort), uses VIDEO_CROSSPOST_TACTICS
 * For regular social channels, uses CROSSPOST_TACTICS
 *
 * @param channel - The channel type (e.g., InstagramReel, FacebookPost)
 * @param scheduleTactics - The tactics enabled in the schedule
 * @returns Array of temporary targets with provider information
 */
export const getCrosspostTargets = (
  channel: string | undefined,
  scheduleTactics: BrandPlanTactic[] | undefined,
): Array<{ provider: string; id: string }> => {
  if (!scheduleTactics) return []

  const isVideo = isVideoChannel(channel as DesignChannel)
  const tactics = isVideo ? VIDEO_CROSSPOST_TACTICS : CROSSPOST_TACTICS

  return scheduleTactics
    .filter((tactic) => tactics.includes(tactic))
    .map((tactic) => ({
      provider: TACTIC_TO_PROVIDER[tactic],
      id: uuid4(),
    }))
}
